<template>
  <div class="home">
    <PopUp v-if="displayPopup === true" :dataProps="googleData" />
    <PopUpPremium v-if="displayPopupPremium === true" :dataProps="googleData" />

    <section class="hero-section">
      <div class="container">
        <h1 class="hero-headline">Automatically Bleep Curse Words From Your Videos</h1>
        <p class="hero-subheadline">
          Upload a video or record audio. AI detects every swear word and bleeps
          it for you — no manual editing, no software to install. Get a
          family-friendly version back in minutes.
        </p>
        <div class="hero-ctas">
          <router-link to="/upload-video" class="hero-cta-primary">
            Bleep My Video
          </router-link>
          <router-link to="/upload-audio" class="hero-cta-secondary">
            Record Audio Instead
          </router-link>
        </div>
      </div>
    </section>

    <section class="trust-bar">
      <div class="container">
        <ul class="trust-items">
          <li class="trust-item">
            <i class="fas fa-bolt"></i> Free to try
          </li>
          <li class="trust-item">
            <i class="fas fa-user-slash"></i> No sign-up needed
          </li>
          <li class="trust-item">
            <i class="fas fa-globe"></i> 24 languages
          </li>
          <li class="trust-item">
            <i class="fas fa-desktop"></i> Works in your browser
          </li>
        </ul>
      </div>
    </section>

    <section class="lp-section how-it-works">
      <div class="container">
        <h2 class="lp-section-heading">How It Works</h2>
        <p class="lp-section-subheading">
          Three steps. No editing skills required.
        </p>
        <div class="row">
          <div class="col-md-4">
            <div class="step-card">
              <div class="step-number">1</div>
              <h3>Upload your file</h3>
              <p>
                Drop in a video file or record audio right in your browser. Up
                to 500 MB.
              </p>
            </div>
          </div>
          <div class="col-md-4">
            <div class="step-card">
              <div class="step-number">2</div>
              <h3>AI detects curse words</h3>
              <p>
                Our system transcribes the audio and finds every swear word
                automatically — across 24 languages.
              </p>
            </div>
          </div>
          <div class="col-md-4">
            <div class="step-card">
              <div class="step-number">3</div>
              <h3>Download your bleeped file</h3>
              <p>
                Get a clean, family-friendly version with every curse word
                bleeped out. Ready to share.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="lp-section lp-section-dark">
      <div class="container">
        <h2 class="lp-section-heading">Why bleepme.io?</h2>
        <p class="lp-section-subheading">
          Purpose-built for one job: finding and bleeping curse words
          automatically.
        </p>
        <div class="row benefits-grid">
          <div class="col-md-4 mb-4">
            <div class="benefit-card">
              <div class="benefit-icon">
                <i class="fas fa-magic"></i>
              </div>
              <h3>Fully automatic</h3>
              <p>
                No manual word-spotting. AI transcribes your audio, detects
                profanity, and bleeps it — all in one step.
              </p>
            </div>
          </div>
          <div class="col-md-4 mb-4">
            <div class="benefit-card">
              <div class="benefit-icon">
                <i class="fas fa-desktop"></i>
              </div>
              <h3>Nothing to install</h3>
              <p>
                Works entirely in your browser. No downloads, no plugins, no
                learning curve. Upload and go.
              </p>
            </div>
          </div>
          <div class="col-md-4 mb-4">
            <div class="benefit-card">
              <div class="benefit-icon">
                <i class="fas fa-language"></i>
              </div>
              <h3>24 languages</h3>
              <p>
                Profanity detection across English, Spanish, French, German,
                Arabic, Hindi, Japanese, and 17 more.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="lp-section lp-section-dark">
      <div class="container">
        <h2 class="lp-section-heading">Built for your needs</h2>
        <p class="lp-section-subheading">
          Whether you're protecting your channel or making classroom-safe
          content.
        </p>
        <div class="row audience-grid">
          <div class="col-md-4 mb-4">
            <div class="audience-card">
              <div class="audience-icon">
                <i class="fas fa-video"></i>
              </div>
              <div class="audience-tag">Creators</div>
              <h3>Avoid demonetization</h3>
              <p>
                YouTube penalizes videos with profanity. Bleep every curse word
                before you publish and protect your ad revenue — without hours
                of manual editing.
              </p>
            </div>
          </div>
          <div class="col-md-4 mb-4">
            <div class="audience-card">
              <div class="audience-icon">
                <i class="fas fa-graduation-cap"></i>
              </div>
              <div class="audience-tag">Teachers</div>
              <h3>Classroom-safe content</h3>
              <p>
                Want to show a YouTube video or song in class but worried about
                profanity? Upload it, get a clean version back, and show it with
                confidence.
              </p>
            </div>
          </div>
          <div class="col-md-4 mb-4">
            <div class="audience-card">
              <div class="audience-icon">
                <i class="fas fa-laugh"></i>
              </div>
              <div class="audience-tag">Just for fun</div>
              <h3>Bleep it for laughs</h3>
              <p>
                Record yourself, bleep the words you can't say, and share the
                funny result on TikTok, Reels, or with friends. Free and
                instant.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="sample-link-section">
      <div class="container">
        <router-link to="/sample"
          >See a before &amp; after example &rarr;</router-link
        >
      </div>
    </section>

    <section class="lp-section lp-section-dark">
      <div class="container">
        <h2 class="lp-section-heading">Frequently asked questions</h2>
        <p class="lp-section-subheading">
          Everything you need to know before you upload.
        </p>
        <div class="faq-section" style="max-width: 720px; margin: 0 auto;">
          <div class="faq-item">
            <details>
              <summary>Will it catch every curse word?</summary>
              <div class="faq-answer">
                AI transcription is highly accurate but not perfect. We've
                analyzed 8,637+ curse word instances to optimize detection.
                Edge cases like mumbled or overlapping speech may occasionally be
                missed, but the vast majority of profanity is caught
                automatically.
              </div>
            </details>
          </div>
          <div class="faq-item">
            <details>
              <summary>How much does it cost?</summary>
              <div class="faq-answer">
                It's free to try. No credit card required.
              </div>
            </details>
          </div>
          <div class="faq-item">
            <details>
              <summary>What languages are supported?</summary>
              <div class="faq-answer">
                24 languages including English, Spanish, French, German, Arabic,
                Hindi, Japanese, and more. Select your language before uploading
                for best results.
              </div>
            </details>
          </div>
          <div class="faq-item">
            <details>
              <summary>What file types and sizes are supported?</summary>
              <div class="faq-answer">
                Video files up to 500 MB (MP4, WebM, MOV, MPEG, OGG,
                QuickTime). Audio files up to 120 MB. You can also record audio
                directly in your browser.
              </div>
            </details>
          </div>
          <div class="faq-item">
            <details>
              <summary>Do I need to sign up to use it?</summary>
              <div class="faq-answer">
                No. You can try it right now without creating an account. Just
                upload a file and go. Sign in with Google only if you need a
                higher daily quota.
              </div>
            </details>
          </div>
        </div>
      </div>
    </section>

    <section class="final-cta-section">
      <div class="container">
        <h2>Ready to bleep your video?</h2>
        <p>
          Upload a file and get a family-friendly version back in minutes. No
          sign-up, no software, no credit card.
        </p>
        <div class="final-cta-buttons">
          <router-link to="/upload-video">Bleep My Video</router-link>
          <router-link to="/upload-audio" class="btn-secondary-cta"
            >Record Audio</router-link
          >
        </div>
        <p class="no-credit-card">Free to try — no credit card required</p>
      </div>
    </section>
  </div>
</template>

<script>
import PopUp from "@/components/PopUp.vue";
import PopUpPremium from "@/components/PopUpPremium.vue";
import { useUserStore } from "@/store/user";
import { ref } from "vue";
import { useRouter } from "vue2-helpers/vue-router";

export default {
  setup() {
    const error = ref(null);
    const displayPopupPremium = ref(false);
    const displayPopup = ref(false);

    const store = useUserStore();
    const router = useRouter();

    const handleConvertToGoogle = async () => {
      try {
        await store.convertToGoogle();
        displayPopupPremium.value = false;
      } catch (err) {
        error.value = err.message;
        router.push("/login");
      }
    };

    return {
      store,
      handleConvertToGoogle,
      error,
      displayPopupPremium,
      displayPopup,
    };
  },
  data() {
    return {
      googleData: {
        googleToken: null,
        youtubeAccountName: null,
        youtubeAccountDescription: null,
        youtubeAccountpublishedAt: null,
        youtubeAccountThumbnail: null,
        youtubeAccountCountry: null,
        youtubeAccountId: null,
        youtubeAccountUploadPlaylistId: null,
        youtubeAccountLatestVideos: null,
        email: null,
        errorMessage: null,
        errorAuth: false,
      },
    };
  },
  name: "Home",
  components: {
    PopUp,
    PopUpPremium,
  },
};
</script>
